import React, { ChangeEvent, useState } from "react"
import { useMutation } from "@apollo/client"
import { useSelector } from "react-redux"
import { customerSelect } from "lib/store/services/customer/customerSelector"
import { useAppDispatch } from "lib/store"
import alertActions from "lib/store/services/Alert/AlertSlice"
import dayjs from "dayjs"
import { CustomerOrders } from "../../classes/customer/CustomerOrders"

import Popup from "components/Ui/Popup"
import Input from "components/Ui/Form/Input"
import DELETE_ORDER from "../../graphql/mutations/order/DeleteOrder"
import { Tooltip } from "components/Ui/Tooltip"
import {
  SecureDelete,
  InputWrapper,
  StyledSecondaryButton,
  OrderList
} from "./DeleteCustomerOrders.styled"

import { ReactComponent as LockIcon } from "images/icons/lock.svg"
import { ReactComponent as UnlockIcon } from "images/icons/unlock.svg"
import { ReactComponent as DeleteIcon } from "images/icons/trash-can.svg"

type Props = {
  customerOrders: CustomerOrders
}

const DeleteCustomerOrders = ({ customerOrders }: Props) => {
  const customer = useSelector(customerSelect)
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState<string>("")
  const [deletingOrders, setDeletingOrders] = useState(false)
  const dispatch = useAppDispatch()

  const [deleteOrder] = useMutation(DELETE_ORDER)

  const isOlderThan30Days = (date: string): boolean => {
    if (!date) return false
    const orderDate = dayjs(date)
    const daysAgo = dayjs().subtract(30, "days")
    return orderDate.isBefore(daysAgo)
  }

  const ordersToDelete = customer.orders?.filter((order) => isOlderThan30Days(order.date)) || []
  const hasRecentOrders = customer.orders?.some((order) => !isOlderThan30Days(order.date)) || false

  const onDelete = async () => {
    if (ordersToDelete.length === 0) return

    setDeletingOrders(true)

    let successCount = 0
    let errorCount = 0
    const deletedOrderIds = new Set<string>()

    for (const order of ordersToDelete) {
      try {
        await deleteOrder({
          variables: {
            orderId: order.id
          }
        })
        successCount++
        deletedOrderIds.add(order.id)
      } catch {
        errorCount++
      }
    }

    setDeletingOrders(false)
    setOpen(false)
    setInput("")

    if (successCount > 0) {
      dispatch(
        alertActions.actions.createAlert({
          message: `Successfully deleted ${successCount} order(s)${errorCount > 0 ? `, ${errorCount} failed` : ""}`,
          type: errorCount > 0 ? "default" : "success"
        })
      )

      // Filter out deleted orders from local state
      const remainingOrders = customer.orders?.filter(order => !deletedOrderIds.has(order.id)) || []
      customerOrders.setOrders(remainingOrders)
    }

    if (errorCount > 0 && successCount === 0) {
      dispatch(
        alertActions.actions.createAlert({
          message: `Failed to delete ${errorCount} order(s)`,
          type: "error"
        })
      )
    }
  }

  const closePopup = () => {
    setOpen(false)
    setInput("")
  }

  const matchInput = () => {
    return input === "delete"
  }

  if (!customer.email || !customer.orders || customer.orders.length === 0) {
    return null
  }

  return (
    <>
      <StyledSecondaryButton
        handleClick={() => setOpen(true)}
        type="button"
        disabled={hasRecentOrders}
        data-tooltip-id="delete-orders-tooltip"
        data-tooltip-content={hasRecentOrders ? "Cannot delete orders - customer has orders within the last 30 days" : undefined}
      >
        <DeleteIcon />
        Delete Customer Orders
      </StyledSecondaryButton>
      {hasRecentOrders && <Tooltip id="delete-orders-tooltip" />}
      {open && (
        <Popup
          title="Delete Customer Orders"
          subtitle={`You are about to delete ${ordersToDelete.length} order(s) for ${customer.email}. This is irreversible.`}
          handleCloseClick={closePopup}
          handleOkClick={onDelete}
          type="delete"
          loading={deletingOrders}
          disableOkButton={!matchInput()}
        >
          <SecureDelete>
            <p>
              Type &quot;<b>delete</b>&quot; to confirm deletion:
            </p>
            <InputWrapper>
              {matchInput() ? <UnlockIcon /> : <LockIcon />}
              <Input
                name="deleteName"
                onChange={(e: ChangeEvent<HTMLInputElement>) => setInput(e.target.value)}
              />
            </InputWrapper>
          </SecureDelete>
          <OrderList>
            <p>
              <strong>Orders to be deleted:</strong>
            </p>
            {ordersToDelete.map((order) => (
              <p key={order.id}>
                {order.reference} - {dayjs(order.date).format("YYYY-MM-DD HH:mm")}
              </p>
            ))}
          </OrderList>
        </Popup>
      )}
    </>
  )
}

export default DeleteCustomerOrders
